class LogosCarousel extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector('.logos-carousel__track');
    if (!this.track) {
      console.error('LogosCarousel: there is no carousel track element.');
      return;
    }

    this.autoplay = this.dataset.autoplay !== 'false';
    const defaultSpeed = -0.8;
    this.speed = this.autoplay ? parseFloat(this.dataset.speed) || defaultSpeed : 0;

    this.isDragging = false;
    this.startPos = 0;
    this.position = 0;
    this.animationFrameId = null;

    this.animate = this.animate.bind(this);
    this.dragStart = this.dragStart.bind(this);
    this.dragMove = this.dragMove.bind(this);
    this.dragEnd = this.dragEnd.bind(this);
  }

  connectedCallback() {
    if (!this.track || this.speed === 0) return;
    this.initializeCarousel();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  initializeCarousel() {
    const list = this.track.querySelector('.logos-carousel__list');
    if (!list || list.children.length === 0) return;

    const images = list.querySelectorAll('img');
    const promises = [...images].map(image => {
      image.loading = 'eager';
      return new Promise(resolve => {
        if (image.complete) {
          resolve();
        } else {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        }
      });
    });

    Promise.all(promises).then(() => {
      this.listWidth = list.scrollWidth;

      const clone = list.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);

      this.addEventListeners();
      this.animate();
    });
  }

  addEventListeners() {
    this.track.addEventListener('mousedown', this.dragStart);
    this.track.addEventListener('touchstart', this.dragStart, { passive: false });

    window.addEventListener('mousemove', this.dragMove);
    window.addEventListener('touchmove', this.dragMove, { passive: false });

    window.addEventListener('mouseup', this.dragEnd);
    window.addEventListener('touchend', this.dragEnd);
  }

  removeEventListeners() {
    if (!this.track) return;
    this.track.removeEventListener('mousedown', this.dragStart);
    this.track.removeEventListener('touchstart', this.dragStart);

    window.removeEventListener('mousemove', this.dragMove);
    window.removeEventListener('touchmove', this.dragMove);

    window.removeEventListener('mouseup', this.dragEnd);
    window.removeEventListener('touchend', this.dragEnd);
  }

  animate() {
    if (!this.isDragging) {
      this.position += this.speed;

      if (this.speed < 0 && this.position < -this.listWidth) {
        this.position += this.listWidth;
      } else if (this.speed > 0 && this.position > 0) {
        this.position -= this.listWidth;
      }
    }

    this.track.style.transform = `translateX(${this.position}px)`;
    this.animationFrameId = requestAnimationFrame(this.animate);
  }

  dragStart(e) {
    e.preventDefault();
    this.isDragging = true;
    this.track.classList.add('grabbing');
    this.startPos = (e.touches ? e.touches[0].pageX : e.pageX) - this.position;
    cancelAnimationFrame(this.animationFrameId);
  }

  dragMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const currentX = e.touches ? e.touches[0].pageX : e.pageX;
    this.position = currentX - this.startPos;
    this.track.style.transform = `translateX(${this.position}px)`;
  }

  dragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.classList.remove('grabbing');
    this.animate();
  }
}

customElements.define('logos-carousel', LogosCarousel);